import {
    Observable,
    of as observableOf,
    Subscription,
    throwError as observableThrowError,
    timer as observableTimer
} from 'rxjs';

import {filter, map, timeInterval, withLatestFrom} from 'rxjs/operators';
/**
 * Created by christiancueni on 23/05/18.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {IS_APP} from '../app.constants';
import {Store} from '@ngrx/store';
import {AppState, getAppOnline, getGroupState, getKlassenbuchState} from '../reducers';
import {KlassenbuchState} from '../reducers/klassenbuch';
import {Group, ViewEvent} from '../models';
import {PrefetchNextWeeksAction} from '../actions/klassenbuch';

const KLASSENBUCH_KEY = 'klassenbuch';
const GROUPS_KEY = 'groups';
const ACCESS_DICT_KEY = 'accessList'
const EVENT_DICT_KEY = 'eventList'

const EXPIRY_DELTA = 1000 * 60 * 60 * 24 * 7; // 7 days in milliseconds
const PREFETCH_INTERVAL = 1000 * 60 * 10;

@Injectable()
export class OfflineStorageService {

    private klassenbuchState: KlassenbuchState;
    private intervalSubscription: Subscription;

    constructor(private store: Store<AppState>) {
    }

    public initDehydration() {
        if (IS_APP) {
            this.store.select(getKlassenbuchState).pipe(
                withLatestFrom(this.store.select(getAppOnline)),
                filter(data => data[1]), // check if the app is online
                map(data => data[0]),)
                .subscribe((klassenbuchState: KlassenbuchState) => {
                    this.klassenbuchState = klassenbuchState;
                    localStorage.setItem(KLASSENBUCH_KEY, JSON.stringify(this.klassenbuchState));

                    if (klassenbuchState.calendarDays[0] != undefined) {
                        this.storeEventsForCurrentWeek();
                        this.removeExpiredData();
                    }
                });

            this.store.select(getGroupState).subscribe(groupState => localStorage.setItem(GROUPS_KEY, JSON.stringify(groupState)));
            setTimeout(() => this.store.dispatch(new PrefetchNextWeeksAction()), 5000); // wait for everything to setup. No need to rush things
            this.startInterval();
        }
    }

    private unsubscribe() {
        if (this.intervalSubscription) {
            this.intervalSubscription.unsubscribe();
        }
    }

    public stopInterval() {
        this.unsubscribe();
    }

    private startInterval() {
        this.unsubscribe();
        this.intervalSubscription = observableTimer(0, PREFETCH_INTERVAL).pipe(timeInterval())
            .subscribe(() => {
                this.store.dispatch(new PrefetchNextWeeksAction());
            });
    }

    // returns an observable so that all effects catch errors the same way
    public getEventFromStorage(id: string): Observable<ViewEvent> {
        const eventDict = this.getEventDictFromStorage();
        return eventDict[+id] ? observableOf(ViewEvent.fromJson(eventDict[+id])) : observableThrowError('no event');
    }

    private storeEventsForCurrentWeek() {
        const eventsForWeek = this.getEventsForWeek(this.klassenbuchState.days);
        this.storeEventsForWeek(this.klassenbuchState.calendarDays[0], eventsForWeek)
    }

    public storeEventsForWeek(weekStart: string, eventsForWeek: ViewEvent[]) {
        const eventIdsForWeek = eventsForWeek.map(event => event.id);
        this.storeEventIdsForWeek(
            weekStart,
            eventIdsForWeek
        );
        this.storeEvents(eventsForWeek);
    }

    public storeEvents(events: ViewEvent[]) {
        let eventDict = this.getEventDictFromStorage();
        events.map(event => eventDict[event.id] = event);
        localStorage.setItem(EVENT_DICT_KEY, JSON.stringify(eventDict));
    }

    public getEventsForWeek(days: ViewEvent[][]): ViewEvent[] {
        return days.reduce((eventsForWeek, day) => {
            return eventsForWeek.concat(day);
        }, [])
    }

    public storeEventIdsForWeek(startDate: string, eventIds: number[]) {
        localStorage.setItem(startDate, JSON.stringify(eventIds));
        this.saveLastAccessForWeek(startDate);
    }

    private saveLastAccessForWeek(startDate: string) {
        const now = new Date();
        let weekAccessList = this.getAccessFromStorage();
        weekAccessList[startDate] = now.toJSON();
        this.storeAccessToStorage(weekAccessList);
    }

    public getEventDictFromStorage(): Object {
        return JSON.parse(localStorage.getItem(EVENT_DICT_KEY)) || {};
    }

    private getAccessFromStorage(): Object {
        return JSON.parse(localStorage.getItem(ACCESS_DICT_KEY)) || [];
    }

    private storeAccessToStorage(weekAccessDict: Object) {
        localStorage.setItem(ACCESS_DICT_KEY, JSON.stringify(weekAccessDict));
    }

    public getWeekFromStorage(weekdays: string[], groups: Group[]): ViewEvent[][] {
        const eventsForWeek = this.getEventsForWeekFromStorage(weekdays[0]);
        const filteredEventsByGroup = this.filterEventsByGroups(eventsForWeek, groups);
        return this.createDaysForState(weekdays, filteredEventsByGroup);
    }

    private getEventsForWeekFromStorage(date: string): ViewEvent[] {
        const eventIds = this.getEventIdsForWeekFromStorage(date);
        const allEventsList = this.getEventDictFromStorage();

        return eventIds.reduce((events, eventId) => {
            if (allEventsList[eventId]) {
                events.push(ViewEvent.fromJson((allEventsList[eventId])));
            }
            return events;
        }, [])
    }

    private getEventIdsForWeekFromStorage(date: string): number[] {
        return JSON.parse(localStorage.getItem(date)) || [];
    }

    private createDaysForState(weekdays: string[], events: ViewEvent[]): ViewEvent[][] {
        return weekdays.map((date) => {
            return events.filter(event => date === event.date)
        })
    }

    private filterEventsByGroups(eventsForWeek: ViewEvent[], groups: Group[]) {
        const groupIds = groups.map(group => group.id);
        return eventsForWeek.filter(event => groupIds.indexOf(event.group) > -1);
    }

    private removeExpiredData() {
        const now = new Date();
        const accessDict = this.getAccessFromStorage();

        const {weeksToRemove, nonExpiredWeeks} = this.getWeeksToRemove(now, accessDict);
        this.storeAccessToStorage(nonExpiredWeeks);
        this.removeWeeksAndEvents(weeksToRemove)
    }

    private getWeeksToRemove(now: Date, accessDict: Object): { weeksToRemove: string[], nonExpiredWeeks: Object } {
        let nonExpiredWeeks = {};
        const weeksToRemove = Object.keys(accessDict).reduce((weeksToRemove, weekId) => { // weekId = YYYY-MM-DD of the week's Monday
            if (this.isExpired(now, new Date(accessDict[weekId]))) {
                weeksToRemove.push(weekId);
            } else {
                nonExpiredWeeks[weekId] = accessDict[weekId];
            }
            return weeksToRemove;
        }, []);

        return {
            weeksToRemove,
            nonExpiredWeeks
        }
    }

    private isExpired(now, lastAccess) {
        return now - lastAccess > EXPIRY_DELTA;
    }

    private removeWeeksAndEvents(weekToRemove: string[]) {
        const eventIdsToRemove = weekToRemove.reduce((allEventIds, weekId) => {
            const eventIdsForWeek = this.getEventIdsForWeekFromStorage(weekId);
            localStorage.removeItem(weekId);
            return allEventIds.concat(eventIdsForWeek);
        }, []);

        this.updateEventsDict(eventIdsToRemove);
    }

    public updateEventsDict(eventIdsToRemove: number[]) {
        const eventsDict = this.getEventDictFromStorage();
        const newEventsDict = Object.keys(eventsDict).reduce((newEventDict, eventId) => {
            if (eventIdsToRemove.indexOf(+eventId) < 0) {
                newEventDict[eventId] = eventsDict[eventId];
            }
            return newEventDict;
        }, {});

        localStorage.setItem(EVENT_DICT_KEY, JSON.stringify(newEventsDict));
    }

}
